import * as React from 'react';

import { CloseIcon } from '@notino/react-styleguide';
import { IStock } from '@notino/shared/definitions/custom-definitions';

import ErrorBoundary from '@components/ErrorBoundary';
import PriceLabel from '@components/PriceLabel';
import { IVariantsProps } from '@containers/ProductDetailContainer/model';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { variantInfo, dataLabelsProp } from '@helpers/utils';
import { HorizontalChevron } from '@sharedComponents/Icons/HorizontalChevron';

import { useIsDualPriceForCroatia } from '../../hooks/useIsDualPriceForCroatia';
import { useNewPdDesignEnabled } from '../../hooks/useNewPdDesignEnabled';
import { VARIANT_CHEVRON_SIZE } from '../Variants/VariantsShadesDrawer/constants';
import { useHasStressOnVoucherDiscount } from '../VoucherDiscount/useHasStressOnVoucherDiscount';

import { ExpandedVariants } from './ExpandedVariants';
import {
  SectionWrapper,
  Wrapper,
  Select,
  SelectedVariantName,
  Text,
  CloseIcon as CloseIconWrapper,
  CloseWrapper,
  ChevronIconWrapper,
  PriceAndName,
  Prices,
  OldPriceWrapper,
  OldCurrencyWrapper,
  PriceWrapper,
  CurrencyWrapper,
  DualPriceWrapper,
  DualPrice,
  DualPriceSplitter,
  SmallerPriceWrapper,
  SmallerCurrencyWrapper,
} from './styled';
import { TagDiscount } from './TagDiscount';
import { trackSelectOpen } from './tracking';
import { VariantItem } from './VariantItem';

export interface IVariantsInSelectboxProps extends IVariantsProps {
  renderTagDiscount: boolean;
}

export const VariantsInSelectbox = ({
  variants,
  selectedId,
  locale,
  renderTagDiscount,
}: IVariantsInSelectboxProps) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { getTimeFromInit } = useTrackingContext();
  const newDesign = useNewPdDesignEnabled();
  const {
    isDualPriceForCroatia,
    getLeftSideFormattedPrice,
    getRightSideFormattedPrice,
    leftSideCurrency,
    rightSideCurrency,
  } = useIsDualPriceForCroatia();

  const handleExpand = (e: React.SyntheticEvent<HTMLElement>) => {
    e.stopPropagation();
    if (!isExpanded) {
      trackSelectOpen(getTimeFromInit());
    }
    setIsExpanded(!isExpanded);
  };

  React.useEffect(() => {
    setIsExpanded(false);
  }, [variants, selectedId]);

  const selectedVariant = variants.find(
    (variant) => variant.webId === selectedId
  );
  const text = variantInfo(selectedVariant);
  const inStock = selectedVariant.stockAvailability.code !== IStock.outOfStock;
  const isSmall = useHasStressOnVoucherDiscount();

  return (
    <ErrorBoundary>
      <SectionWrapper id="pdVariantsSelectbox" newDesign={newDesign}>
        {renderTagDiscount && <TagDiscount variant={selectedVariant} />}
        <Wrapper newDesign={newDesign}>
          <Select
            newDesign={newDesign}
            onClick={handleExpand}
            className="pd-variant-selected"
            aria-live="assertive"
            tabIndex={0}
            isExpanded={isExpanded}
            {...dataLabelsProp(selectedVariant.attributes)}
          >
            <PriceAndName>
              {inStock && !isDualPriceForCroatia && (
                <Prices>
                  <PriceLabel
                    id="pd-price"
                    price={selectedVariant.price}
                    wrapperComponent={
                      newDesign
                        ? isSmall
                          ? SmallerPriceWrapper
                          : PriceWrapper
                        : OldPriceWrapper
                    }
                    currencyComponent={
                      newDesign
                        ? isSmall
                          ? SmallerCurrencyWrapper
                          : CurrencyWrapper
                        : OldCurrencyWrapper
                    }
                  />
                </Prices>
              )}

              {inStock && isDualPriceForCroatia && (
                <Prices>
                  <DualPriceWrapper id="pd-price">
                    <DualPrice newDesign={newDesign} isSmall={isSmall}>
                      {getLeftSideFormattedPrice(selectedVariant.price.value)}{' '}
                      <span>{leftSideCurrency}</span>
                    </DualPrice>
                    <DualPriceSplitter newDesign={newDesign} isSmall={isSmall}>
                      /
                    </DualPriceSplitter>
                    <DualPrice newDesign={newDesign} isSmall={isSmall}>
                      {getRightSideFormattedPrice(selectedVariant.price.value)}{' '}
                      <span>{rightSideCurrency}</span>
                    </DualPrice>
                  </DualPriceWrapper>
                </Prices>
              )}

              <SelectedVariantName>
                <Text newDesign={newDesign} title={text}>
                  {text}
                </Text>
              </SelectedVariantName>
            </PriceAndName>
            <ChevronIconWrapper type="button" newDesign={newDesign}>
              <HorizontalChevron
                isOpened={isExpanded}
                width={VARIANT_CHEVRON_SIZE}
                height={VARIANT_CHEVRON_SIZE}
                color="icon.secondary"
              />
            </ChevronIconWrapper>
          </Select>

          {isExpanded && (
            <ExpandedVariants
              isExpanded={isExpanded}
              onOuterClick={handleExpand}
            >
              <CloseWrapper newDesign={newDesign}>
                <CloseIconWrapper onClick={handleExpand} newDesign={newDesign}>
                  <CloseIcon
                    color="icon.tertiary"
                    width={newDesign ? '1.25rem' : '32px'}
                    height={newDesign ? '1.25rem' : '32px'}
                  />
                </CloseIconWrapper>
              </CloseWrapper>
              {variants.map((variant) => (
                <VariantItem
                  variant={variant}
                  locale={locale}
                  isSelected={variant.webId === selectedId}
                  key={variant.webId}
                />
              ))}
            </ExpandedVariants>
          )}
        </Wrapper>
      </SectionWrapper>
    </ErrorBoundary>
  );
};
